import { Link } from 'react-router-dom';
import { Bookmark, Flame } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDynamicTranslate } from '@/hooks/useDynamicTranslate';

function formatHRDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const m = ['siječnja','veljače','ožujka','travnja','svibnja','lipnja','srpnja','kolovoza','rujna','listopada','studenog','prosinca'];
  return `${d.getDate()}. ${m[d.getMonth()]} ${d.getFullYear()}.`;
}

function useArticleText(article) {
  const { i18n } = useTranslation();
  const title = useDynamicTranslate(
    article?.title_translations?.[i18n.language] || article?.title_hr || ''
  );
  const excerpt = useDynamicTranslate(
    article?.excerpt_translations?.[i18n.language] || article?.excerpt_hr || ''
  );
  const catName = useDynamicTranslate(article?.category?.name_hr || '');
  return { title, excerpt, catName };
}

function Badge({ slug, name, color }) {
  const bg = color || '#dc2626';
  return (
    <span className="inline-block text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm"
          style={{ backgroundColor: bg }}>
      {name}
    </span>
  );
}

// Hero card #1 — dominant left, full-bleed image
function LeadCard({ article }) {
  const { t } = useTranslation();
  const { title, excerpt, catName } = useArticleText(article);
  if (!article) return null;
  const hasCover = !!article.cover_image_url;
  const showRedFallback = !hasCover && article.is_breaking;
  return (
    <Link to={`/clanak/${article.slug}`}
          data-testid="magazine-hero-lead"
          className="group relative block h-full min-h-[420px] rounded-xl overflow-hidden">
      {showRedFallback ? (
        <div className="absolute inset-0 bg-red-600 opacity-50" data-testid="breaking-red-fallback" />
      ) : (
        <img src={article.cover_image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200'}
             alt={title} loading="eager" fetchpriority="high" decoding="async"
             className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
      )}
      {article.is_breaking && !showRedFallback && (
        <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      <div className="absolute top-5 left-5 flex items-center gap-2">
        {article.is_breaking && (
          <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-1 rounded-sm">
            <Flame size={12} /> {t('home.udarno')}
          </span>
        )}
        {article.is_pinned && !article.is_breaking && (
          <span className="inline-flex items-center gap-1 bg-amber-500 text-zinc-900 text-[10px] font-bold uppercase px-2 py-1 rounded-sm" data-testid="magazine-pinned-badge">
            ★ Istaknuto
          </span>
        )}
        {article.category && <Badge slug={article.category.slug} name={catName || article.category.name_hr} color={article.category.color} />}
      </div>
      <button className="absolute top-5 right-5 p-2 text-white/80 hover:text-white rounded-full bg-black/30 backdrop-blur"
              aria-label={t('article.save') || 'Save'} onClick={e => e.preventDefault()}>
        <Bookmark size={16} />
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
        <h2 className="text-3xl md:text-4xl font-black leading-[1.08] mb-3 line-clamp-3"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>{title}</h2>
        {excerpt && <p className="text-sm text-white/85 line-clamp-2 mb-2 max-w-2xl">{excerpt}</p>}
        <p className="text-[11px] text-white/70 uppercase tracking-wide">{formatHRDate(article.published_at || article.publish_at || article.created_at)}</p>
      </div>
    </Link>
  );
}

// Compact image-on-top card used for 4 secondary heroes
function SubCard({ article, compact = false }) {
  const { t } = useTranslation();
  const { title, catName } = useArticleText(article);
  if (!article) return null;
  const hasCover = !!article.cover_image_url;
  const showRedFallback = !hasCover && article.is_breaking;
  return (
    <Link to={`/clanak/${article.slug}`}
          data-testid={`magazine-hero-sub-${article.id}`}
          className="group block rounded-xl overflow-hidden bg-white dark:deep-black-card border border-transparent dark:border-white/5 hover:shadow-lg transition-shadow">
      <div className={`relative overflow-hidden ${compact ? 'h-32' : 'h-40'}`}>
        {showRedFallback ? (
          <div className="absolute inset-0 bg-red-600 opacity-50" data-testid="breaking-red-fallback-sub" />
        ) : (
          <img src={article.cover_image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600'}
               alt={title} loading="lazy" decoding="async"
               className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        )}
        {article.is_breaking && !showRedFallback && (
          <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay-sub" />
        )}
        {article.is_breaking && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-1 rounded-sm z-10">
            <Flame size={12} /> {t('home.udarno')}
          </span>
        )}
        {article.category && !showRedFallback && (
          <div className="absolute top-2.5 left-2.5">
            <Badge slug={article.category.slug} name={catName || article.category.name_hr} color={article.category.color} />
          </div>
        )}
      </div>
      <div className="p-3.5">
        <h3 className={`${compact ? 'text-sm' : 'text-[15px]'} font-bold text-gray-900 dark:text-gray-50 leading-snug line-clamp-3 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors`}>
          {title}
        </h3>
        <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {formatHRDate(article.published_at || article.publish_at || article.created_at)}
        </p>
      </div>
    </Link>
  );
}

/**
 * 5-card magazine hero. Layout (desktop):
 *  ┌────────────────────┬──────────┬──────────┐
 *  │                    │  sub #1  │  sub #2  │
 *  │      LEAD (1)      ├──────────┼──────────┤
 *  │                    │  sub #3  │  sub #4  │
 *  └────────────────────┴──────────┴──────────┘
 * Mobile stacks linearly.
 */
export default function HomeHeroMagazine({ articles }) {
  if (!articles || articles.length === 0) return null;
  const lead = articles[0];
  const subs = articles.slice(1, 5);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-4 min-h-[420px]" data-testid="home-hero-magazine">
      <div className="h-full"><LeadCard article={lead} /></div>
      <div className="grid grid-cols-2 gap-4 h-full">
        {subs.map(a => <SubCard key={a.id} article={a} />)}
      </div>
    </div>
  );
}
